import {
  normalizeSlaAgreementCustomerSubmission,
  normalizeSlaAgreementEvent,
  normalizeSlaAgreementRevisionRequest,
  normalizeSlaAgreementSendPackage,
  normalizeSlaReconciliation,
} from '@/lib/sla/normalize';
import { isPosSystemOption } from '@/lib/constants/pos-systems';
import type {
  LogSlaSigningUiEventResponse,
  OpenSlaSigningSessionResponse,
  RequestSlaSignatureRevisionResponse,
  SlaAgreement,
  SlaAllowedCustomerField,
  SlaAgreementCustomerSubmission,
  SlaAgreementEvent,
  SlaAgreementReconciliation,
  SlaAgreementRevisionRequest,
  SlaAgreementSendPackage,
  SlaAgreementSigner,
  SlaAgreementSignerToken,
  SlaSigningUiEventDetails,
  SlaSigningUiEventType,
  SlaAgreementWritebackResult,
  SubmitSlaCustomerSignatureResponse,
} from '@/types/sla-agreements';
import { SLA_ALLOWED_CUSTOMER_FIELDS, SLA_SIGNING_UI_EVENT_TYPES } from '@/types/sla-agreements';
import { mockSlaAgreementsService } from './mock-sla-agreements';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const CUSTOMER_ALLOWED_FIELDS: readonly SlaAllowedCustomerField[] = SLA_ALLOWED_CUSTOMER_FIELDS;
const SIGNING_UI_EVENT_TYPES = new Set<SlaSigningUiEventType>(SLA_SIGNING_UI_EVENT_TYPES);
const FALLBACK_STORE_ID = 'store-1';

const mockSendPackages: SlaAgreementSendPackage[] = [];
const mockEvents: SlaAgreementEvent[] = [];
const mockRevisionRequests: SlaAgreementRevisionRequest[] = [];

function normalizeSignatureComparableText(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function createSignerToken(
  agreement: SlaAgreement,
  sendPackage: SlaAgreementSendPackage,
  signer: SlaAgreementSigner
) {
  const now = new Date().toISOString();
  const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const tokenId = `sla-token-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const rawToken = `mock-signature-token-${tokenId}`;
  const token: SlaAgreementSignerToken = {
    id: tokenId,
    agreement_id: agreement.id,
    send_package_id: sendPackage.id,
    signer_id: signer.id,
    status: 'active',
    token_hash: rawToken,
    expires_at: expiresAt,
    revoked_at: null,
    expired_at: null,
    superseded_at: null,
    created_at: now,
    updated_at: now,
    created_by: null,
    updated_by: null,
  };

  signer.tokens = [token];
  return { token, rawToken };
}

function getPackageById(sendPackageId: string | null | undefined): SlaAgreementSendPackage | null {
  if (!sendPackageId) {
    return null;
  }

  return mockSendPackages.find((item) => item.id === sendPackageId) || null;
}

function getCustomerSigner(sendPackage: SlaAgreementSendPackage | null): SlaAgreementSigner | null {
  return sendPackage?.signers.find((signer) => signer.role === 'customer') || null;
}

function getTpbSigner(sendPackage: SlaAgreementSendPackage | null): SlaAgreementSigner | null {
  return sendPackage?.signers.find((signer) => signer.role === 'tpb') || null;
}

function getOpenRevisionRequest(sendPackageId: string): SlaAgreementRevisionRequest | null {
  return (
    mockRevisionRequests.find(
      (request) => request.send_package_id === sendPackageId && request.status === 'open'
    ) || null
  );
}

function isExpiredAt(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  return new Date(value).getTime() <= Date.now();
}

function buildSigningSessionResponse(input: {
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  signer: SlaAgreementSigner;
}): OpenSlaSigningSessionResponse {
  const openRevisionRequest = getOpenRevisionRequest(input.sendPackage.id);

  return {
    ok: true,
    session: {
      agreementId: input.agreement.id,
      agreementNumber: input.agreement.agreement_number,
      sendPackageId: input.sendPackage.id,
      signerId: input.signer.id,
      signer: {
        name: input.signer.name || '',
        email: input.signer.email || '',
        title: input.signer.title,
      },
      draftPayload: input.sendPackage.draft_payload_json,
      reconciliation: input.agreement.reconciliation_json,
      templateKey: input.sendPackage.template_key,
      templateVersion: input.sendPackage.template_version,
      allowedCustomerFields: [...CUSTOMER_ALLOWED_FIELDS],
      canRequestRevision: !openRevisionRequest && input.signer.status !== 'signed',
      canSign:
        !openRevisionRequest &&
        input.signer.status !== 'signed' &&
        input.signer.status !== 'revision_requested' &&
        input.agreement.status !== 'revision_requested',
    },
  };
}

async function resolveSigningStateByToken(rawToken: string): Promise<{
  agreement: SlaAgreement;
  sendPackage: SlaAgreementSendPackage;
  signer: SlaAgreementSigner;
  token: SlaAgreementSignerToken;
} | null> {
  for (const sendPackage of mockSendPackages) {
    for (const signer of sendPackage.signers) {
      const token = signer.tokens.find((item) => item.token_hash === rawToken);
      if (!token) {
        continue;
      }

      const agreement = await mockSlaAgreementsService.getById(token.agreement_id);
      if (!agreement) {
        return null;
      }

      return {
        agreement,
        sendPackage,
        signer,
        token,
      };
    }
  }

  return null;
}

function updateSignerOnPackage(
  sendPackage: SlaAgreementSendPackage,
  signerId: string,
  updater: (signer: SlaAgreementSigner) => SlaAgreementSigner
) {
  sendPackage.signers = sendPackage.signers.map((signer) => {
    if (signer.id !== signerId) {
      return signer;
    }

    return updater({
      ...signer,
      tokens: signer.tokens.map((token) => ({ ...token })),
    });
  });
}

function supersedeOpenRevisionRequests(sendPackageId: string) {
  const now = new Date().toISOString();
  for (let index = 0; index < mockRevisionRequests.length; index += 1) {
    const request = mockRevisionRequests[index];
    if (!request || request.send_package_id !== sendPackageId || request.status !== 'open') {
      continue;
    }

    mockRevisionRequests[index] = normalizeSlaAgreementRevisionRequest({
      ...request,
      status: 'superseded',
      resolved_at: now,
      resolved_by: null,
    } as unknown as Record<string, unknown>);
  }
}

async function expireMockSigningState(agreementId: string): Promise<SlaAgreement> {
  const agreement = await mockSlaAgreementsService.getById(agreementId);
  if (!agreement?.current_send_package_id) {
    if (!agreement) {
      throw new Error('Agreement not found.');
    }
    return agreement;
  }

  const sendPackage = getPackageById(agreement.current_send_package_id);
  const now = new Date().toISOString();

  if (sendPackage) {
    sendPackage.status = 'expired';
    sendPackage.expired_at = now;
    sendPackage.signers = sendPackage.signers.map((signer) => ({
      ...signer,
      status: signer.status === 'signed' ? signer.status : 'expired',
      expired_at: signer.status === 'signed' ? signer.expired_at : now,
      tokens: signer.tokens.map((token) => ({
        ...token,
        status: token.status === 'active' ? 'expired' : token.status,
        expired_at: token.status === 'active' ? now : token.expired_at,
      })),
    }));
  }

  return mockSlaAgreementsService.update(agreement.id, {
    status: 'expired',
    signature_request_expires_at: sendPackage?.expires_at || now,
  });
}

function validateCustomerSubmission(
  sendPackage: SlaAgreementSendPackage,
  submission: SlaAgreementCustomerSubmission
): { normalizedSubmission: SlaAgreementCustomerSubmission; reconciliation: SlaAgreementReconciliation } {
  const normalizedSubmission = normalizeSlaAgreementCustomerSubmission(submission);
  if (!normalizedSubmission) {
    throw new Error('Customer submission is required.');
  }

  if (!normalizedSubmission.consent_accepted) {
    throw new Error('Electronic signature consent is required.');
  }

  if (!normalizedSubmission.execution_date) {
    throw new Error('Execution date is required.');
  }

  if (!normalizedSubmission.legal_entity_name.trim()) {
    throw new Error('Legal entity name is required.');
  }

  if (
    !sendPackage.draft_payload_json.pos_system?.trim() &&
    !normalizedSubmission.pos_system?.trim()
  ) {
    throw new Error('POS system is required.');
  }

  if (
    !sendPackage.draft_payload_json.pos_system?.trim() &&
    !isPosSystemOption(normalizedSubmission.pos_system)
  ) {
    throw new Error('POS system must be a supported option.');
  }

  if (!normalizedSubmission.signer_name.trim()) {
    throw new Error('Signer name is required.');
  }

  if (!normalizedSubmission.signer_title.trim()) {
    throw new Error('Signer title is required.');
  }

  if (!normalizedSubmission.signer_email.trim()) {
    throw new Error('Signer email is required.');
  }

  if (!normalizedSubmission.notice_address.trim()) {
    throw new Error('Notice address is required.');
  }

  if (!normalizedSubmission.customer_initials?.trim()) {
    throw new Error('Customer initials are required.');
  }

  if (!normalizedSubmission.customer_initial_date) {
    throw new Error('Customer initial date is required.');
  }

  const frozenStores = sendPackage.draft_payload_json.stores;
  const hasFallbackStoreSubmission =
    frozenStores.length === 0 &&
    normalizedSubmission.stores.length === 1 &&
    normalizedSubmission.stores[0]?.store_id === FALLBACK_STORE_ID;

  if (
    (frozenStores.length === 0 && !hasFallbackStoreSubmission) ||
    (frozenStores.length > 0 && normalizedSubmission.stores.length !== frozenStores.length)
  ) {
    throw new Error('Customer store rows must match the frozen agreement package.');
  }

  const expectedStores =
    frozenStores.length > 0
      ? frozenStores.map((store) => ({
          store_id: store.id,
          location_id: store.expected_location_id,
        }))
      : [{ store_id: FALLBACK_STORE_ID, location_id: null }];

  const reconciliation = normalizeSlaReconciliation({
    execution_date: normalizedSubmission.execution_date,
    legal_entity_name: normalizedSubmission.legal_entity_name,
    pos_system: normalizedSubmission.pos_system || sendPackage.draft_payload_json.pos_system,
    signer_name: normalizedSubmission.signer_name,
    signer_title: normalizedSubmission.signer_title,
    signer_email: normalizedSubmission.signer_email,
    notice_address: normalizedSubmission.notice_address,
    customer_initials: normalizedSubmission.customer_initials,
    customer_initial_date: normalizedSubmission.customer_initial_date,
    stores: expectedStores.map((store, index) => {
      const submittedStore = normalizedSubmission.stores[index];

      if (!submittedStore || submittedStore.store_id !== store.store_id) {
        throw new Error('Customer store rows must preserve the frozen store order.');
      }

      if (!submittedStore.store_name.trim()) {
        throw new Error(`Store ${index + 1} name is required.`);
      }

      if (!submittedStore.store_address?.trim()) {
        throw new Error(`Store ${index + 1} address is required.`);
      }

      if (!submittedStore.tentative_installation_date) {
        throw new Error(`Store ${index + 1} tentative installation date is required.`);
      }

      return {
        store_id: store.store_id,
        store_name: submittedStore.store_name,
        store_address: submittedStore.store_address,
        tentative_installation_date: submittedStore.tentative_installation_date,
        location_id: store.location_id,
      };
    }),
  });

  if (!reconciliation) {
    throw new Error('Customer submission could not be normalized.');
  }

  return {
    normalizedSubmission: {
      ...normalizedSubmission,
      pos_system:
        sendPackage.draft_payload_json.pos_system || normalizedSubmission.pos_system,
      stores: reconciliation.stores,
      signature_text: normalizedSubmission.signer_name.trim(),
    },
    reconciliation,
  };
}

export const mockSlaAgreementSigningService = {
  async createSendPackage(input: { agreement: SlaAgreement }): Promise<SlaAgreementSendPackage> {
    await delay(100);
    const currentVersion =
      mockSendPackages
        .filter((sendPackage) => sendPackage.agreement_id === input.agreement.id)
        .reduce((max, sendPackage) => Math.max(max, sendPackage.package_version), 0) + 1;
    const now = new Date().toISOString();
    const sendPackage: SlaAgreementSendPackage = {
      id: `sla-send-package-${Date.now()}`,
      agreement_id: input.agreement.id,
      package_version: currentVersion,
      status: 'created',
      agreement_number: input.agreement.agreement_number,
      template_key: input.agreement.template_key,
      template_version: input.agreement.template_version,
      generated_file_path: input.agreement.generated_file_path || '',
      draft_payload_json: input.agreement.draft_payload_json,
      email_id: null,
      sent_at: null,
      expires_at: null,
      revoked_at: null,
      expired_at: null,
      superseded_at: null,
      created_at: now,
      updated_at: now,
      created_by: null,
      updated_by: null,
      signers: [],
    };

    mockSendPackages.push(sendPackage);
    return sendPackage;
  },

  async createSigners(input: { agreement: SlaAgreement; sendPackage: SlaAgreementSendPackage }): Promise<SlaAgreementSigner[]> {
    await delay(50);
    const now = new Date().toISOString();
    const signers: SlaAgreementSigner[] = [
      {
        id: `sla-signer-customer-${Date.now()}`,
        agreement_id: input.agreement.id,
        send_package_id: input.sendPackage.id,
        role: 'customer',
        status: 'pending',
        internal_user_id: null,
        reminder_count: 0,
        first_reminder_sent_at: null,
        last_reminder_sent_at: null,
        name: input.agreement.draft_payload_json.signer_name,
        title: input.agreement.draft_payload_json.signer_title,
        email: input.agreement.draft_payload_json.signer_email,
        emailed_at: null,
        viewed_at: null,
        started_at: null,
        consented_at: null,
        signed_at: null,
        submission_json: null,
        signature_type: null,
        signature_text: null,
        last_ip_address: null,
        last_user_agent: null,
        revoked_at: null,
        expired_at: null,
        superseded_at: null,
        created_at: now,
        updated_at: now,
        created_by: null,
        updated_by: null,
        tokens: [],
      },
      {
        id: `sla-signer-tpb-${Date.now()}`,
        agreement_id: input.agreement.id,
        send_package_id: input.sendPackage.id,
        role: 'tpb',
        status: 'pending',
        internal_user_id: 'mock-user',
        reminder_count: 0,
        first_reminder_sent_at: null,
        last_reminder_sent_at: null,
        name: 'TPB Admin',
        title: null,
        email: 'admin@thepeakbeyond.com',
        emailed_at: null,
        viewed_at: null,
        started_at: null,
        consented_at: null,
        signed_at: null,
        submission_json: null,
        signature_type: null,
        signature_text: null,
        last_ip_address: null,
        last_user_agent: null,
        revoked_at: null,
        expired_at: null,
        superseded_at: null,
        created_at: now,
        updated_at: now,
        created_by: null,
        updated_by: null,
        tokens: [],
      },
    ];

    input.sendPackage.signers = signers;
    return signers;
  },

  async issueCustomerToken(input: {
    agreementId: string;
    sendPackageId: string;
    signerId: string;
  }): Promise<{ token: SlaAgreementSignerToken; rawToken: string }> {
    await delay(50);
    const agreement = await mockSlaAgreementsService.getById(input.agreementId);
    const sendPackage = getPackageById(input.sendPackageId);
    const signer = sendPackage?.signers.find((item) => item.id === input.signerId);

    if (!agreement || !sendPackage || !signer) {
      throw new Error('Unable to issue mock signature token.');
    }

    return createSignerToken(agreement, sendPackage, signer);
  },

  async logEvent(input: {
    agreementId: string;
    eventType: string;
    sendPackageId?: string | null;
    signerId?: string | null;
    tokenId?: string | null;
    packageVersion?: number | null;
    actorType?: 'system' | 'user';
    actorUserId?: string | null;
    actorLabel?: string | null;
    details?: Record<string, unknown> | null;
  }): Promise<SlaAgreementEvent> {
    await delay(20);
    const event = normalizeSlaAgreementEvent({
      id: `sla-event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      agreement_id: input.agreementId,
      send_package_id: input.sendPackageId ?? null,
      signer_id: input.signerId ?? null,
      token_id: input.tokenId ?? null,
      package_version: input.packageVersion ?? null,
      actor_type: input.actorType ?? 'system',
      actor_user_id: input.actorUserId ?? null,
      actor_label: input.actorLabel ?? null,
      event_type: input.eventType,
      details_json: input.details ?? {},
      created_at: new Date().toISOString(),
    });
    mockEvents.unshift(event);
    return event;
  },

  async getCurrentSendPackage(agreementId: string): Promise<SlaAgreementSendPackage | null> {
    await delay(50);
    const agreement = await mockSlaAgreementsService.getById(agreementId);
    if (!agreement?.current_send_package_id) {
      return null;
    }

    const sendPackage = getPackageById(agreement.current_send_package_id);
    if (!sendPackage) {
      return null;
    }

    if (sendPackage.status === 'sent' && isExpiredAt(sendPackage.expires_at)) {
      await this.expireSendPackage(agreementId);
      return getPackageById(agreement.current_send_package_id);
    }

    return sendPackage;
  },

  async getTimeline(agreementId: string): Promise<SlaAgreementEvent[]> {
    await delay(50);
    return mockEvents.filter((event) => event.agreement_id === agreementId);
  },

  async revokeActiveSendPackage(agreementId: string): Promise<SlaAgreement> {
    await delay(100);
    const agreement = await mockSlaAgreementsService.getById(agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    const sendPackage = getPackageById(agreement.current_send_package_id);
    const now = new Date().toISOString();
    if (sendPackage) {
      sendPackage.status = 'revoked';
      sendPackage.revoked_at = now;
      sendPackage.signers = sendPackage.signers.map((signer) => ({
        ...signer,
        status: signer.status === 'signed' ? signer.status : 'revoked',
        revoked_at: signer.status === 'signed' ? signer.revoked_at : now,
        tokens: signer.tokens.map((token) => ({
          ...token,
          status: token.status === 'active' ? 'revoked' : token.status,
          revoked_at: token.status === 'active' ? now : token.revoked_at,
        })),
      }));
      supersedeOpenRevisionRequests(sendPackage.id);
    }

    await this.logEvent({
      agreementId,
      eventType: 'send_package_revoked',
      sendPackageId: sendPackage?.id ?? null,
      packageVersion: sendPackage?.package_version ?? null,
      actorType: 'user',
      actorLabel: 'Mock TPB Admin',
    });

    return mockSlaAgreementsService.update(agreementId, {
      status: 'generated',
      current_send_package_id: null,
      signature_request_revoked_at: now,
      signature_request_expires_at: null,
      signature_request_email_id: null,
    });
  },

  async expireSendPackage(agreementId: string): Promise<SlaAgreement> {
    await delay(100);
    const agreement = await expireMockSigningState(agreementId);
    const sendPackage = getPackageById(agreement.current_send_package_id);

    await this.logEvent({
      agreementId,
      eventType: 'send_package_expired',
      sendPackageId: sendPackage?.id ?? null,
      packageVersion: sendPackage?.package_version ?? null,
      actorType: 'system',
    });

    return agreement;
  },

  async sendSignatureRequest(input: { agreementId: string; resend?: boolean }): Promise<{
    agreement: SlaAgreement;
    sendPackage: SlaAgreementSendPackage;
  }> {
    await delay(150);
    const agreement = await mockSlaAgreementsService.getById(input.agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    if (!agreement.generated_file_path) {
      throw new Error('Generate the agreement PDF before sending for signature.');
    }

    if (
      ![
        'generated',
        'signature_requested',
        'customer_viewed',
        'customer_in_progress',
        'revision_requested',
        'expired',
      ].includes(agreement.status)
    ) {
      throw new Error('This agreement is not in a sendable state for signature requests.');
    }

    if (!agreement.draft_payload_json.signer_email?.trim()) {
      throw new Error('Customer signer email is required before sending for signature.');
    }

    const existingPackage = agreement.current_send_package_id
      ? getPackageById(agreement.current_send_package_id)
      : null;

    const sendPackage = await this.createSendPackage({ agreement });
    const signers = await this.createSigners({ agreement, sendPackage });
    const customerSigner = signers.find((signer) => signer.role === 'customer');
    if (!customerSigner) {
      throw new Error('Customer signer could not be created.');
    }

    const issuedToken = await this.issueCustomerToken({
      agreementId: agreement.id,
      sendPackageId: sendPackage.id,
      signerId: customerSigner.id,
    });
    const now = new Date().toISOString();

    sendPackage.status = 'sent';
    sendPackage.sent_at = now;
    sendPackage.email_id = `mock-email-${Date.now()}`;
    sendPackage.expires_at = issuedToken.token.expires_at;

    sendPackage.signers = sendPackage.signers.map((signer) =>
      signer.id === customerSigner.id
        ? {
            ...signer,
            status: 'emailed',
            emailed_at: now,
            tokens: signer.tokens,
          }
        : signer
    );

    if (input.resend && existingPackage) {
      existingPackage.status = 'superseded';
      existingPackage.superseded_at = now;
      existingPackage.signers = existingPackage.signers.map((signer) => ({
        ...signer,
        status: signer.status === 'signed' ? signer.status : 'superseded',
        superseded_at: signer.status === 'signed' ? signer.superseded_at : now,
        tokens: signer.tokens.map((token) => ({
          ...token,
          status: token.status === 'active' ? 'superseded' : token.status,
          superseded_at: token.status === 'active' ? now : token.superseded_at,
        })),
      }));
      supersedeOpenRevisionRequests(existingPackage.id);
    }

    const updatedAgreement = await mockSlaAgreementsService.update(agreement.id, {
      status: 'signature_requested',
      current_send_package_id: sendPackage.id,
      signature_requested_at: now,
      signature_request_expires_at: sendPackage.expires_at,
      signature_request_revoked_at: null,
      signature_request_email_id: sendPackage.email_id,
    });

    await this.logEvent({
      agreementId: agreement.id,
      eventType: input.resend ? 'signature_request_resent' : 'signature_request_sent',
      sendPackageId: sendPackage.id,
      signerId: customerSigner.id,
      tokenId: issuedToken.token.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorLabel: 'Mock TPB Admin',
      details: {
        to: customerSigner.email,
        emailId: sendPackage.email_id,
      },
    });

    return {
      agreement: updatedAgreement,
      sendPackage: normalizeSlaAgreementSendPackage(sendPackage as unknown as Record<string, unknown>),
    };
  },

  async openSigningSession(token: string): Promise<OpenSlaSigningSessionResponse> {
    await delay(80);
    const signingState = await resolveSigningStateByToken(token);
    if (!signingState) {
      return { ok: false, error: 'invalid_token' };
    }

    let { agreement, sendPackage, signer, token: signerToken } = signingState;

    if (signerToken.status === 'revoked' || sendPackage.status === 'revoked') {
      return { ok: false, error: 'revoked' };
    }

    if (signerToken.status === 'superseded' || sendPackage.status === 'superseded') {
      return { ok: false, error: 'superseded' };
    }

    if (
      signerToken.status === 'expired' ||
      sendPackage.status === 'expired' ||
      isExpiredAt(signerToken.expires_at) ||
      isExpiredAt(sendPackage.expires_at)
    ) {
      await this.expireSendPackage(agreement.id);
      return { ok: false, error: 'expired' };
    }

    if (agreement.status === 'voided') {
      return { ok: false, error: 'voided' };
    }

    if (agreement.status === 'superseded' || agreement.current_send_package_id !== sendPackage.id) {
      return { ok: false, error: 'superseded' };
    }

    if (
      signer.status === 'signed' ||
      agreement.status === 'customer_signed' ||
      agreement.status === 'awaiting_tpb_signature' ||
      agreement.status === 'completed'
    ) {
      return { ok: false, error: 'already_signed' };
    }

    const now = new Date().toISOString();
    if (!signer.viewed_at) {
      updateSignerOnPackage(sendPackage, signer.id, (currentSigner) => ({
        ...currentSigner,
        status: 'viewed',
        viewed_at: now,
        updated_at: now,
      }));
      agreement = await mockSlaAgreementsService.update(agreement.id, {
        status: 'customer_viewed',
      });
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_customer_session_opened',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorLabel: signer.name || signer.email || 'Customer signer',
      });
    } else if (
      !signer.started_at &&
      !getOpenRevisionRequest(sendPackage.id)
    ) {
      updateSignerOnPackage(sendPackage, signer.id, (currentSigner) => ({
        ...currentSigner,
        status: currentSigner.status === 'revision_requested' ? currentSigner.status : 'in_progress',
        started_at: currentSigner.started_at || now,
        updated_at: now,
      }));
      if (agreement.status !== 'revision_requested') {
        agreement = await mockSlaAgreementsService.update(agreement.id, {
          status: 'customer_in_progress',
        });
      }
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_customer_session_started',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorLabel: signer.name || signer.email || 'Customer signer',
      });
    }

    sendPackage = getPackageById(sendPackage.id) || sendPackage;
    signer = getCustomerSigner(sendPackage) || signer;
    signerToken = signer.tokens[0] || signerToken;

    return buildSigningSessionResponse({
      agreement,
      sendPackage,
      signer,
    });
  },

  async markSigningSessionStarted(token: string): Promise<OpenSlaSigningSessionResponse> {
    return this.openSigningSession(token);
  },

  async logSigningUiEvent(input: {
    token: string;
    eventType: SlaSigningUiEventType;
    details?: SlaSigningUiEventDetails | null;
  }): Promise<LogSlaSigningUiEventResponse> {
    await delay(40);
    const signingState = await resolveSigningStateByToken(input.token);
    if (!signingState) {
      return { ok: false, error: 'invalid_token', message: 'Signing session not found.' };
    }

    const { agreement, sendPackage, signer, token: signerToken } = signingState;

    if (!SIGNING_UI_EVENT_TYPES.has(input.eventType)) {
      return {
        ok: false,
        error: 'validation_error',
        message: 'Unsupported signing UI event type.',
      };
    }

    await this.logEvent({
      agreementId: agreement.id,
      eventType: input.eventType,
      sendPackageId: sendPackage.id,
      signerId: signer.id,
      tokenId: signerToken.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorLabel: signer.name || signer.email || 'Customer signer',
      details: input.details ? { ...input.details } : {},
    });

    return {
      ok: true,
      agreementId: agreement.id,
      eventType: input.eventType,
    };
  },

  async submitCustomerSignature(input: {
    token: string;
    submission: SlaAgreementCustomerSubmission;
  }): Promise<SubmitSlaCustomerSignatureResponse> {
    await delay(120);
    const signingState = await resolveSigningStateByToken(input.token);
    if (!signingState) {
      return { ok: false, error: 'invalid_token', message: 'Signing session not found.' };
    }

    let { agreement, sendPackage, signer, token: signerToken } = signingState;

    if (
      signer.status === 'signed' ||
      agreement.status === 'customer_signed' ||
      agreement.status === 'awaiting_tpb_signature' ||
      agreement.status === 'completed'
    ) {
      return {
        ok: true,
        agreementId: agreement.id,
        status: agreement.status === 'completed' ? 'completed' : 'awaiting_tpb_signature',
        alreadySigned: true,
        message: 'This agreement has already been signed.',
      };
    }

    if (signerToken.status === 'revoked' || sendPackage.status === 'revoked') {
      return { ok: false, error: 'revoked', message: 'This signing link has been revoked.' };
    }

    if (signerToken.status === 'superseded' || sendPackage.status === 'superseded') {
      return { ok: false, error: 'superseded', message: 'A newer signing link has already been issued.' };
    }

    if (
      signerToken.status === 'expired' ||
      sendPackage.status === 'expired' ||
      isExpiredAt(signerToken.expires_at) ||
      isExpiredAt(sendPackage.expires_at)
    ) {
      await this.expireSendPackage(agreement.id);
      return { ok: false, error: 'expired', message: 'This signing link has expired.' };
    }

    if (agreement.status === 'voided') {
      return { ok: false, error: 'voided', message: 'This agreement is no longer available.' };
    }

    if (agreement.current_send_package_id !== sendPackage.id) {
      return { ok: false, error: 'superseded', message: 'A newer signing link has already been issued.' };
    }

    if (agreement.status === 'revision_requested' || signer.status === 'revision_requested') {
      return {
        ok: false,
        error: 'validation_error',
        message: 'Revisions have already been requested for this agreement.',
      };
    }

    try {
      const { normalizedSubmission, reconciliation } = validateCustomerSubmission(sendPackage, input.submission);
      const now = new Date().toISOString();
      const hadStarted = Boolean(signer.started_at);

      updateSignerOnPackage(sendPackage, signer.id, (currentSigner) => ({
        ...currentSigner,
        status: 'signed',
        viewed_at: currentSigner.viewed_at || now,
        started_at: currentSigner.started_at || now,
        consented_at: now,
        signed_at: now,
        submission_json: normalizedSubmission,
        signature_type: 'typed',
        signature_text: normalizedSubmission.signature_text,
        last_ip_address: '127.0.0.1',
        last_user_agent: 'mock-browser',
        updated_at: now,
      }));

      agreement = await mockSlaAgreementsService.update(agreement.id, {
        status: 'awaiting_tpb_signature',
        reconciliation_json: reconciliation,
      });

      if (!hadStarted) {
        await this.logEvent({
          agreementId: agreement.id,
          eventType: 'agreement_customer_session_started',
          sendPackageId: sendPackage.id,
          signerId: signer.id,
          tokenId: signerToken.id,
          packageVersion: sendPackage.package_version,
          actorType: 'user',
          actorLabel: signer.name || signer.email || 'Customer signer',
        });
      }

      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_customer_consent_accepted',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorLabel: signer.name || signer.email || 'Customer signer',
      });
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_customer_signature_submitted',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorLabel: signer.name || signer.email || 'Customer signer',
        details: {
          signedAt: now,
        },
      });

      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_awaiting_tpb_signature',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'system',
      });

      return {
        ok: true,
        agreementId: agreement.id,
        status: 'awaiting_tpb_signature',
        alreadySigned: false,
        message: 'Signature submitted successfully.',
      };
    } catch (error) {
      await this.logEvent({
        agreementId: agreement.id,
        eventType: 'agreement_customer_submit_rejected',
        sendPackageId: sendPackage.id,
        signerId: signer.id,
        tokenId: signerToken.id,
        packageVersion: sendPackage.package_version,
        actorType: 'user',
        actorLabel: signer.name || signer.email || 'Customer signer',
        details: {
          error: error instanceof Error ? error.message : String(error),
        },
      });

      return {
        ok: false,
        error: 'validation_error',
        message: error instanceof Error ? error.message : 'Failed to validate customer signature.',
      };
    }
  },

  async requestSignatureRevision(input: {
    token: string;
    message: string;
  }): Promise<RequestSlaSignatureRevisionResponse> {
    await delay(100);
    const signingState = await resolveSigningStateByToken(input.token);
    if (!signingState) {
      return { ok: false, error: 'invalid_token', message: 'Signing session not found.' };
    }

    let { agreement, sendPackage, signer, token: signerToken } = signingState;

    if (!input.message.trim()) {
      return { ok: false, error: 'validation_error', message: 'Revision notes are required.' };
    }

    if (
      signer.status === 'signed' ||
      agreement.status === 'customer_signed' ||
      agreement.status === 'awaiting_tpb_signature' ||
      agreement.status === 'completed'
    ) {
      return {
        ok: false,
        error: 'already_signed',
        message: 'This agreement has already been signed and can no longer request revisions.',
      };
    }

    if (signerToken.status === 'revoked' || sendPackage.status === 'revoked') {
      return { ok: false, error: 'revoked', message: 'This signing link has been revoked.' };
    }

    if (signerToken.status === 'superseded' || sendPackage.status === 'superseded') {
      return { ok: false, error: 'superseded', message: 'A newer signing link has already been issued.' };
    }

    if (
      signerToken.status === 'expired' ||
      sendPackage.status === 'expired' ||
      isExpiredAt(signerToken.expires_at) ||
      isExpiredAt(sendPackage.expires_at)
    ) {
      await this.expireSendPackage(agreement.id);
      return { ok: false, error: 'expired', message: 'This signing link has expired.' };
    }

    if (agreement.status === 'voided') {
      return { ok: false, error: 'voided', message: 'This agreement is no longer available.' };
    }

    if (agreement.current_send_package_id !== sendPackage.id) {
      return { ok: false, error: 'superseded', message: 'A newer signing link has already been issued.' };
    }

    const existingOpenRequest = getOpenRevisionRequest(sendPackage.id);
    if (existingOpenRequest) {
      return {
        ok: true,
        agreementId: agreement.id,
        status: 'revision_requested',
        message: 'Revisions have already been requested for this agreement.',
      };
    }

    const now = new Date().toISOString();
    const revisionRequest = normalizeSlaAgreementRevisionRequest({
      id: `sla-revision-request-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      agreement_id: agreement.id,
      send_package_id: sendPackage.id,
      signer_id: signer.id,
      status: 'open',
      message: input.message.trim(),
      created_at: now,
      resolved_at: null,
      resolved_by: null,
    } as unknown as Record<string, unknown>);
    mockRevisionRequests.unshift(revisionRequest);

    updateSignerOnPackage(sendPackage, signer.id, (currentSigner) => ({
      ...currentSigner,
      status: 'revision_requested',
      viewed_at: currentSigner.viewed_at || now,
      started_at: currentSigner.started_at || now,
      updated_at: now,
    }));

    agreement = await mockSlaAgreementsService.update(agreement.id, {
      status: 'revision_requested',
    });

    await this.logEvent({
      agreementId: agreement.id,
      eventType: 'agreement_customer_revision_requested',
      sendPackageId: sendPackage.id,
      signerId: signer.id,
      tokenId: signerToken.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorLabel: signer.name || signer.email || 'Customer signer',
      details: {
        message: input.message.trim(),
      },
    });

    return {
      ok: true,
      agreementId: agreement.id,
      status: 'revision_requested',
      message: 'Revision request submitted.',
    };
  },

  async getOpenRevisionRequest(sendPackageId: string): Promise<SlaAgreementRevisionRequest | null> {
    await delay(40);
    return getOpenRevisionRequest(sendPackageId);
  },

  async openTpbCountersignSession(agreementId: string): Promise<{
    agreement: SlaAgreement;
    sendPackage: SlaAgreementSendPackage;
    tpbSigner: SlaAgreementSigner;
    customerSigner: SlaAgreementSigner;
    openRevisionRequest: SlaAgreementRevisionRequest | null;
  }> {
    await delay(80);
    const agreement = await mockSlaAgreementsService.getById(agreementId);
    if (!agreement?.current_send_package_id) {
      throw new Error('This agreement does not have an active e-sign package.');
    }

    const sendPackage = getPackageById(agreement.current_send_package_id);
    if (!sendPackage) {
      throw new Error('Active send package not found.');
    }

    if (agreement.status === 'customer_signed') {
      await mockSlaAgreementsService.update(agreement.id, {
        status: 'awaiting_tpb_signature',
      });
    }

    const refreshedAgreement = (await mockSlaAgreementsService.getById(agreementId)) || agreement;
    if (refreshedAgreement.status !== 'awaiting_tpb_signature') {
      throw new Error('This agreement is not ready for TPB countersign.');
    }

    const customerSigner = getCustomerSigner(sendPackage);
    const tpbSigner = getTpbSigner(sendPackage);
    if (!customerSigner || !tpbSigner) {
      throw new Error('The active send package is missing required signer records.');
    }

    const openRevisionRequest = getOpenRevisionRequest(sendPackage.id);
    if (openRevisionRequest || customerSigner.status === 'revision_requested') {
      throw new Error('Resolve the customer revision request before countersigning.');
    }

    if (customerSigner.status !== 'signed') {
      throw new Error('Customer signature must be completed before TPB countersign.');
    }

    await this.logEvent({
      agreementId: refreshedAgreement.id,
      eventType: 'agreement_tpb_countersign_opened',
      sendPackageId: sendPackage.id,
      signerId: tpbSigner.id,
      packageVersion: sendPackage.package_version,
      actorType: 'user',
      actorUserId: tpbSigner.internal_user_id,
      actorLabel: tpbSigner.name || 'TPB Admin',
    });

    return {
      agreement: refreshedAgreement,
      sendPackage,
      tpbSigner,
      customerSigner,
      openRevisionRequest,
    };
  },

  async submitTpbCountersignAndFinalize(input: {
    agreementId: string;
    signatureText: string;
    buildFinalizedArtifact: (context: {
      agreement: SlaAgreement;
      sendPackage: SlaAgreementSendPackage;
      reconciliation: SlaAgreementReconciliation;
      customerSigner: SlaAgreementSigner;
      tpbSigner: SlaAgreementSigner;
    }) => Promise<{ blob: Blob; file: File; filename: string }>;
  }): Promise<{
    agreement: SlaAgreement;
    sendPackage: SlaAgreementSendPackage;
    writebackResult: SlaAgreementWritebackResult;
    deliveryEmailId: string | null;
    finalizedFilePath: string;
  }> {
    await delay(120);
    const session = await this.openTpbCountersignSession(input.agreementId);
    const normalizedSignatureText = input.signatureText.trim();
    if (!normalizedSignatureText) {
      throw new Error('Typed TPB signature is required.');
    }

    if (
      normalizeSignatureComparableText(normalizedSignatureText) !==
      normalizeSignatureComparableText(session.tpbSigner.name)
    ) {
      throw new Error('Typed TPB signature must match the assigned TPB signer name.');
    }

    const now = new Date().toISOString();
    updateSignerOnPackage(session.sendPackage, session.tpbSigner.id, (currentSigner) => ({
      ...currentSigner,
      status: 'signed',
      signed_at: currentSigner.signed_at || now,
      signature_type: 'typed',
      signature_text: normalizedSignatureText,
      last_user_agent: 'mock-browser',
      updated_at: now,
    }));

    const artifact = await input.buildFinalizedArtifact({
      agreement: session.agreement,
      sendPackage: session.sendPackage,
      reconciliation:
        normalizeSlaReconciliation(session.agreement.reconciliation_json) ||
        ({
          execution_date: null,
          legal_entity_name: '',
          pos_system: null,
          signer_name: '',
          signer_title: '',
          signer_email: '',
          notice_address: '',
          customer_initials: null,
          customer_initial_date: null,
          stores: [],
        } satisfies SlaAgreementReconciliation),
      customerSigner: session.customerSigner,
      tpbSigner: getTpbSigner(session.sendPackage) || session.tpbSigner,
    });
    const finalizedFilePath = `sla-agreements/${session.agreement.id}/finalized/${Date.now()}-${artifact.file.name}`;
    const writebackResult: SlaAgreementWritebackResult = {
      location_updates: [],
      contact_updates: [],
      equipment: null,
      warnings: [],
    };

    const completedAgreement = await mockSlaAgreementsService.update(session.agreement.id, {
      status: 'completed',
      finalized_file_path: finalizedFilePath,
      signed_file_path: finalizedFilePath,
      finalized_at: now,
      writeback_result_json: writebackResult,
      completed_at: now,
      completed_delivery_at: now,
      completed_delivery_email_id: `mock-delivery-${Date.now()}`,
    });

    await this.logEvent({
      agreementId: completedAgreement.id,
      eventType: 'agreement_tpb_signature_submitted',
      sendPackageId: session.sendPackage.id,
      signerId: session.tpbSigner.id,
      packageVersion: session.sendPackage.package_version,
      actorType: 'user',
      actorUserId: session.tpbSigner.internal_user_id,
      actorLabel: session.tpbSigner.name || 'TPB Admin',
    });
    await this.logEvent({
      agreementId: completedAgreement.id,
      eventType: 'agreement_completed',
      sendPackageId: session.sendPackage.id,
      signerId: session.tpbSigner.id,
      packageVersion: session.sendPackage.package_version,
      actorType: 'user',
      actorUserId: session.tpbSigner.internal_user_id,
      actorLabel: session.tpbSigner.name || 'TPB Admin',
    });

    return {
      agreement: completedAgreement,
      sendPackage: session.sendPackage,
      writebackResult,
      deliveryEmailId: completedAgreement.completed_delivery_email_id,
      finalizedFilePath,
    };
  },

  async retryFinalization(input: {
    agreementId: string;
    buildFinalizedArtifact?: (context: {
      agreement: SlaAgreement;
      sendPackage: SlaAgreementSendPackage;
      reconciliation: SlaAgreementReconciliation;
      customerSigner: SlaAgreementSigner;
      tpbSigner: SlaAgreementSigner;
    }) => Promise<{ blob: Blob; file: File; filename: string }>;
  }): Promise<{
    agreement: SlaAgreement;
    sendPackage: SlaAgreementSendPackage;
    writebackResult: SlaAgreementWritebackResult;
    deliveryEmailId: string | null;
    finalizedFilePath: string;
  }> {
    await delay(100);
    const agreement = await mockSlaAgreementsService.getById(input.agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    if (!agreement.finalized_file_path && !input.buildFinalizedArtifact) {
      throw new Error('A finalized artifact builder is required when no finalized file exists.');
    }

    const session = await this.openTpbCountersignSession(input.agreementId);
    const finalizedFilePath = agreement.finalized_file_path || `sla-agreements/${agreement.id}/finalized/mock-retry.pdf`;
    const writebackResult: SlaAgreementWritebackResult = {
      location_updates: [],
      contact_updates: [],
      equipment: null,
      warnings: [],
    };

    const completedAgreement = await mockSlaAgreementsService.update(session.agreement.id, {
      status: 'completed',
      finalized_file_path: finalizedFilePath,
      signed_file_path: finalizedFilePath,
      finalized_at: agreement.finalized_at || new Date().toISOString(),
      writeback_result_json: writebackResult,
      completed_at: new Date().toISOString(),
      completed_delivery_at: new Date().toISOString(),
      completed_delivery_email_id: agreement.completed_delivery_email_id || `mock-delivery-${Date.now()}`,
    });

    return {
      agreement: completedAgreement,
      sendPackage: session.sendPackage,
      writebackResult,
      deliveryEmailId: completedAgreement.completed_delivery_email_id,
      finalizedFilePath,
    };
  },

  async retryCompletedDelivery(input: { agreementId: string }): Promise<SlaAgreement> {
    await delay(60);
    const agreement = await mockSlaAgreementsService.getById(input.agreementId);
    if (!agreement) {
      throw new Error('Agreement not found.');
    }

    return mockSlaAgreementsService.update(agreement.id, {
      completed_delivery_at: new Date().toISOString(),
      completed_delivery_email_id: `mock-delivery-${Date.now()}`,
    });
  },
};
